import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, ShoppingBag, X } from "lucide-react";
import Logo from "@/components/Logo";
import { useStore } from "@/context/StoreContext";

const CATEGORY_LINKS = [
  { label: "Resin Art", cat: "Resin Art" },
  { label: "Macramé", cat: "Macramé" },
  { label: "Pottery", cat: "Ceramics & Pottery" },
  { label: "Candles", cat: "Candles & Fragrance" },
];

export default function Nav() {
  const { count, setOpen } = useStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const goAnchor = (e, id) => {
    e.preventDefault();
    setMobileOpen(false);
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 450);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const links = (
    <>
      <Link
        to="/shop"
        onClick={() => setMobileOpen(false)}
        className="text-sm font-medium transition-colors hover:text-terracotta"
        data-testid="nav-shop-link"
      >
        Shop all
      </Link>
      {CATEGORY_LINKS.map((l, i) => (
        <Link
          key={l.cat}
          to={`/shop?category=${encodeURIComponent(l.cat)}`}
          onClick={() => setMobileOpen(false)}
          className="text-sm font-medium transition-colors hover:text-terracotta"
          data-testid={`nav-category-link-${i}`}
        >
          {l.label}
        </Link>
      ))}
      <a href="#story" onClick={(e) => goAnchor(e, "story")} className="text-sm font-medium transition-colors hover:text-terracotta" data-testid="nav-story-link">
        Our story
      </a>
      <a href="#gifting" onClick={(e) => goAnchor(e, "gifting")} className="text-sm font-medium transition-colors hover:text-terracotta" data-testid="nav-gifting-link">
        Gifting &amp; Bulk
      </a>
    </>
  );

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 border-b border-stone-200 bg-parchment/80 backdrop-blur-xl"
      data-testid="site-nav"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 md:h-20 md:px-8">
        <Link to="/" data-testid="nav-home-link" aria-label="Knot & Cast home">
          <Logo />
        </Link>
        <nav className="hidden items-center gap-8 md:flex">{links}</nav>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setOpen(true)}
            className="relative rounded-full p-2.5 transition-colors hover:bg-cream"
            data-testid="cart-open-btn"
            aria-label="Open cart"
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -right-0.5 -top-0.5 grid h-5 w-5 place-items-center rounded-full bg-terracotta font-mono text-[10px] font-semibold text-white"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="rounded-full p-2.5 transition-colors hover:bg-cream md:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <nav className="flex flex-col gap-4 border-t border-stone-200 bg-parchment px-5 py-6 md:hidden" data-testid="mobile-menu">
          {links}
        </nav>
      )}
    </header>
  );
}
